import type { DueItem, TicketBoardCard, TicketKalender } from "@kundeninfo/api-client";
import { berlinerTag, freieTage, monatBeschriftung, tagBeschriftung, tageZwischen } from "./ticketFormat.js";
import { isOpen } from "./hierarchy.js";
import { effortShare, shownEffort, type EffortShare } from "./effort.js";
import { TIMELINE_TEXT } from "./timelineTexts.js";
import { compareDueItems, DUE_SOURCES, knownItems, recedesBehindTicket } from "../termine/dueItems.js";
import type { DueSource } from "../termine/dueItems.js";

/**
 * MODELL DER ZEITACHSE (Spec Plan-Ansicht S5–S10, S13, S14, S20; Plan P5, P9, P15) — reine Funktionen, kein React,
 * kein DOM, kein ECharts. Gruppen, Zeilen, Spuren, Balkenenden, Zustände, Fenster und Layout werden hier gerechnet;
 * Zeilenspalte (DOM) und Zeichenflächen (Canvas) lesen dieselben Zahlen, damit beide auf denselben Pixel fallen.
 * Alle Zeitwerte sind Berliner Kalendertage als ISO-Zeichenkette (`2026-10-17`); gerechnet wird mit `Date.UTC` wie in
 * `ticketFormat.ts`, ein Tag reicht auf der Achse von `dayMs(tag)` bis `dayMs(tag) + 1 Tag`.
 */

/**
 * ÜBERGANG bis Aufgabe 2 gemergt ist: die Antwort von `GET /api/tickets/timeline-rows` (S2) in der Form, die der Brief
 * von Aufgabe 2 als Schema nennt (Team `{ id, shortCode, name, active, members }`, Person `{ id, name, email }`).
 * Nach dem Merge durch den erzeugten Typ aus `@kundeninfo/api-client` ersetzen.
 */
export type TicketTimelineRows = {
  teams: { id: string; shortCode: string; name: string; active: boolean;
           members: { id: string; name: string; email: string | null }[] }[];
  users: { id: string; name: string; email: string | null }[];
};

export const LANE_PX = 22, ROW_PAD_PX = 6, ROW_MIN_PX = 28, HEADER_PX = 36, AXIS_PX = 44, LABEL_PX = 220, LABEL_PX_PHONE = 48,
  LANES_MAX = 6, BARS_MAX = 1500, ROWS_MAX = 300, GROUPS_OPEN_MAX = 8, CANVAS_MAX_PX = 4000, TEXT_MIN_PX = 60, EDGE_MIN_PX = 24,
  DEPTH_MAX = 3;
/** Terminebene (Spec D24, T19): höchstens 1 500 Marken im Fenster; ein Abruf `due-items` umfasst höchstens 400 Tage. */
export const MARKERS_MAX = 1500, DUE_WINDOW_MAX_DAYS = 400;

// ── Kalendertage ───────────────────────────────────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;

export function dayMs(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return Date.UTC(y!, m! - 1, d!);
}
const dayAt = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
export const addDays = (day: string, n: number): string => dayAt(dayMs(day) + n * DAY_MS);
/** 0 = Montag … 6 = Sonntag. */
export const weekday = (day: string): number => (new Date(dayMs(day)).getUTCDay() + 6) % 7;
const isFirst = (day: string): boolean => day.endsWith("-01");
const spanOf = (w: TimelineWindow): number => tageZwischen(w.from, w.to) + 1;
const daysOf = (w: TimelineWindow): string[] => Array.from({ length: spanOf(w) }, (_, i) => addDays(w.from, i));

/** Kalenderwoche nach ISO 8601: die Woche gehört zum Jahr ihres Donnerstags. */
export function isoWeek(day: string): number {
  const thursday = dayMs(day) + (3 - weekday(day)) * DAY_MS;
  return Math.floor((thursday - Date.UTC(new Date(thursday).getUTCFullYear(), 0, 1)) / DAY_MS / 7) + 1;
}

const BERLIN_CLOCK = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Berlin", hour: "2-digit", minute: "2-digit",
  second: "2-digit", hourCycle: "h23" });

/** Der Zeitpunkt „jetzt“ auf der Achse: der Tag `heute` plus die Berliner Uhrzeit von `now` (F13). */
export function nowOnAxis(heute: string, now: Date): number {
  const parts = BERLIN_CLOCK.formatToParts(now);
  const part = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return dayMs(heute) + ((part("hour") * 60 + part("minute")) * 60 + part("second")) * 1000 + now.getUTCMilliseconds();
}

// ── Fenster und Stufen (S14) ───────────────────────────────────────────────────────────────────────────────────

export const TIMELINE_STAGES = ["DAY", "WEEK", "MONTH", "QUARTER"] as const;
export type TimelineStage = typeof TIMELINE_STAGES[number];
export const STAGE_SPAN_DAYS: Readonly<Record<TimelineStage, number>> = { DAY: 7, WEEK: 35, MONTH: 92, QUARTER: 366 };
/** Der sichtbare Zeitraum, beide Tage inklusiv. */
export type TimelineWindow = { from: string; to: string };

/** Erster Tag des Fensters um den Anker A (S14-Tabelle). */
const STAGE_START: Readonly<Record<TimelineStage, (anchor: string) => string>> = {
  DAY: (a) => addDays(a, -1),
  WEEK: (a) => addDays(a, -weekday(a) - 7),                    // Montag der Vorwoche
  MONTH: (a) => { const d = addDays(a, -14); return addDays(d, -weekday(d)); },   // Montag ≤ A − 14
  QUARTER: (a) => `${addDays(a, -60).slice(0, 8)}01`,          // Monatserster ≤ A − 60
};

export function windowFor(stage: TimelineStage, anchor: string): TimelineWindow {
  const from = STAGE_START[stage](anchor);
  return { from, to: addDays(from, STAGE_SPAN_DAYS[stage] - 1) };
}

/** Blättern um die halbe Spanne; das Fenster bleibt gleich breit. */
export function shiftWindow(w: TimelineWindow, direction: -1 | 1): TimelineWindow {
  const n = direction * Math.max(1, Math.floor(spanOf(w) / 2));
  return { from: addDays(w.from, n), to: addDays(w.to, n) };
}

/** Die Stufe, deren Spanne das Fenster genau hat — sonst `null` („frei“ nach Pinch oder Rahmen). */
export function stageOf(w: TimelineWindow): TimelineStage | null {
  return TIMELINE_STAGES.find((s) => STAGE_SPAN_DAYS[s] === spanOf(w)) ?? null;
}

/** Die Stufe mit der nächstliegenden Spanne (gedrücktes Segment bei „frei“); bei Gleichstand die feinere. */
export function nearestStage(w: TimelineWindow): TimelineStage {
  const n = spanOf(w);
  const distance = (s: TimelineStage) => Math.abs(STAGE_SPAN_DAYS[s] - n);
  return TIMELINE_STAGES.reduce((best, s) => (distance(s) < distance(best) ? s : best));
}

/** Ein Fenster aus den Grenzen einer Zoom-Geste (ms, Ende exklusiv), auf ganze Tage gerundet, mindestens 2 Tage. */
export function windowFromMs(startValue: number, endValue: number): TimelineWindow {
  const from = Math.round(startValue / DAY_MS);
  const toExclusive = Math.max(Math.round(endValue / DAY_MS), from + 2);
  return { from: dayAt(from * DAY_MS), to: dayAt((toExclusive - 1) * DAY_MS) };
}

/** Raster je Tag einer Stufe: stark, leise oder keine Linie (P5, S14-Tabelle). */
const GRID: Readonly<Record<TimelineStage, (day: string) => boolean | null>> = {
  DAY: () => false,
  WEEK: (d) => weekday(d) === 0,
  MONTH: (d) => (isFirst(d) ? true : weekday(d) === 0 ? false : null),
  QUARTER: (d) => (isFirst(d) ? (Number(d.slice(5, 7)) - 1) % 3 === 0 : null),
};

/** Rasterlinien am Beginn der Tage im Fenster; `strong` = breite Linie. Ein freies Fenster folgt der nächsten Stufe. */
export function gridLines(w: TimelineWindow, stage: TimelineStage | null): { day: string; strong: boolean }[] {
  const rule = GRID[stage ?? nearestStage(w)];
  return daysOf(w).flatMap((day) => { const strong = rule(day); return strong === null ? [] : [{ day, strong }]; });
}

/** Getönte Tage im Fenster: Wochenenden und Feiertage (Name aus dem Kalender des Boards, 132 über `freieTage`). */
export function shading(w: TimelineWindow, kalender: readonly TicketKalender[]): { day: string; holiday: string | null }[] {
  const holidays = freieTage(kalender);
  return daysOf(w).flatMap((day) => {
    const holiday = holidays.get(day)?.name ?? null;
    return holiday !== null || weekday(day) >= 5 ? [{ day, holiday }] : [];
  });
}

/** Beschriftung eines Tages auf der Kopfachse (S14); Tage ohne Beschriftung der Stufe liefern "". Frei (`null`)
 *  beschriftet wie Tag — die Fläche reicht `stage ?? nearestStage(window)` herein, damit Raster und Text passen. */
export function axisLabel(day: string, stage: TimelineStage | null): string {
  switch (stage ?? "DAY") {
    case "DAY": return tagBeschriftung(day);
    case "WEEK": return weekday(day) === 0 ? TIMELINE_TEXT.calendarWeek(isoWeek(day), day) : "";
    case "MONTH": return isFirst(day) ? monatBeschriftung(day) : "";
    case "QUARTER": return !isFirst(day) ? "" : day.slice(5, 7) === "01" ? monatBeschriftung(day) : monatBeschriftung(day).split(" ")[0]!;
  }
}

// ── Balken (S9, S10) ───────────────────────────────────────────────────────────────────────────────────────────

export type BarState = "OVERDUE" | "DUE_TODAY" | "OPEN" | "WAITING" | "DONE";
/** Rangfolge „Schlechtestes oben“ (S10): Spuren, Kürzung S20 und Tastaturwanderung. */
export const BAR_RANK: Readonly<Record<BarState, number>> = { OVERDUE: 0, DUE_TODAY: 1, OPEN: 2, WAITING: 3, DONE: 4 };
type BarCard = Pick<TicketBoardCard, "createdAt" | "resubmitDate" | "dueDate" | "doneAt" | "stateKind">;

export function barState(c: BarCard, heute: string): BarState {
  if (!isOpen(c.stateKind)) return "DONE";
  if (c.dueDate !== null && c.dueDate < heute) return "OVERDUE";
  if (c.dueDate === heute) return "DUE_TODAY";
  return c.stateKind === "WAITING" ? "WAITING" : "OPEN";
}

/** S9: Beginn = Wiedervorlage, sonst Anlage (Berliner Tag); Ende = Fällig bis, erledigt `doneAt` (Altbestand: Fällig
 *  bis, sonst heute), offen ohne Fällig bis heute mit offenem Ende — nur wenn der Balken nicht in der Zukunft beginnt
 *  (künftige Wiedervorlage ohne Fällig bis = Tagesmarke, Ruling M1); `to < from` → Marke an `from`. Vorlauf von der
 *  Anlage, wenn die Wiedervorlage danach liegt (538); Überzug ab dem Ende bis heute, wenn überfällig. */
export function barEnds(c: BarCard, heute: string):
    { from: string; to: string; openEnd: boolean; leadFrom: string | null; overdueFrom: string | null } {
  const created = berlinerTag(c.createdAt);
  const from = c.resubmitDate ?? created;
  const done = !isOpen(c.stateKind);
  const end = done && c.doneAt !== null ? berlinerTag(c.doneAt) : c.dueDate ?? heute;
  const to = end < from ? from : end;
  return { from, to, openEnd: !done && c.dueDate === null && from <= heute,
           leadFrom: c.resubmitDate !== null && c.resubmitDate > created ? created : null,
           overdueFrom: barState(c, heute) === "OVERDUE" && to < heute ? to : null };
}

/** A22: Füllgrad und Überschreitung am Balken aus den angezeigten Werten (A10, 570: am Eltern-Balken der Teilbaum, `sum`). */
export type TimelineEffort = EffortShare & { sum: boolean };

/** A22: `null` bei Plan `NULL` oder 0 (A11) — der Balken sieht dann aus wie ohne Aufwand. */
function effortOf(card: TicketBoardCard): TimelineEffort | null {
  const s = shownEffort(card);
  const share = effortShare(s.plan, s.worked);
  return share === null ? null : { ...share, sum: s.sum };
}

// ── Zeilenmodell (S5–S8, S13, S20) ─────────────────────────────────────────────────────────────────────────────

export type TimelinePerson = { id: string; name: string; email?: string | null };
export type TimelineBar = { id: string; from: string; to: string; openEnd: boolean; leadFrom: string | null; overdueFrom: string | null;
  state: BarState; lane: number; card: TicketBoardCard; children: number; canExpand: boolean; expanded: boolean;
  /** 533: Team-Kennung der Hauptgruppe, wenn dieser Balken eine schraffierte Wiederholung ist, sonst `null`. */
  duplicateOf: string | null; isDuplicate: boolean; effort: TimelineEffort | null };
export type TimelineRowKind = "TEAM" | "PERSON" | "CHILD" | "DUE";
/** Gruppenarten (D24): Teams (Tickets), „Meilensteine“ über den Teams, je zugeschaltete Fremdquelle eine Gruppe darunter. */
export type TimelineGroupKind = "TEAM" | "MILESTONES" | "SOURCE";
/** Eine Marke der Terminebene (D24, T19): `id` = Schlüssel des Eintrags, `from` = sein Tag, `to` = heute bei Überzug
 *  (überfällig vor heute, `overdueFrom` = Tag), sonst der Tag; `lane` wie am Balken (−1 = hinter „+n“). */
export type TimelineMarker = { id: string; item: DueItem; from: string; to: string; state: BarState; lane: number;
  overdueFrom: string | null };
/** `bars` = sichtbare Balken (Spur ≥ 0) in Rangfolge S10 (= Tastaturfolge S15), `hidden` = die übrigen hinter „+n“
 *  (Spur −1), ebenfalls in Rangfolge; `overdue` zählt beide (Fenster). `loaded`/`loadedOverdue` = Tickets der Zeile im
 *  geladenen Stand, unabhängig vom Fenster (S5, 536: Zähler und „frei“ lesen diese Werte; Unterzeile = ihr Ticket).
 *  Terminzeilen (`DUE`, je Unternehmen `companyId`) tragen statt Balken `markers`/`hiddenMarkers` in derselben Teilung,
 *  ihre Zähler zählen die Marken im Fenster; Ticketzeilen haben `[]`, `[]`, `null`. */
export type TimelineRow = { id: string; kind: TimelineRowKind; label: string; person: TimelinePerson | null; depth: number;
  bars: TimelineBar[]; lanes: number; hidden: TimelineBar[]; overdue: number; loaded: number; loadedOverdue: number;
  parentBarId: string | null; markers: TimelineMarker[]; hiddenMarkers: TimelineMarker[]; companyId: string | null };
/** `count`/`overdue` zählen die im Fenster gezeichneten Balken (`bars` + `hidden`, auch Wiederholungen 533), `loaded`/
 *  `loadedOverdue` die Tickets der Gruppe im geladenen Stand (Kopfzahl S5, Wiederholungen je Gruppe) — beide ohne
 *  Unterzeilen. */
export type TimelineGroup = { id: string; kind: TimelineGroupKind; source: DueSource | null;
  team: TicketTimelineRows["teams"][number] | null; label: string; rows: TimelineRow[];
  count: number; overdue: number; loaded: number; loadedOverdue: number; collapsed: boolean };
/** `collapsed` hält Gruppen-Kennungen (`team:<id>`, `team:none`) und `!team:<id>` = ausdrücklich offen (ab der 9. Gruppe).
 *  `filter` (S16, wahlfrei): Team → nur diese Gruppe; Zuständig gesetzt → nur Zeilen mit Tickets im geladenen Stand. */
export type TimelineInput = { cards: readonly TicketBoardCard[]; rows: TicketTimelineRows; heute: string; meId: string;
  window: TimelineWindow; collapsed: ReadonlySet<string>; expanded: ReadonlyMap<string, readonly TicketBoardCard[]>; phone: boolean;
  filter?: { team?: string; assignee?: string } };
/** `bars` = gezeichnete Tickets, jedes einmal (S20, 533); `truncated` = Grenze Balken oder Zeilen überschritten. */
export type Timeline = { groups: TimelineGroup[]; bars: number; rowCount: number; truncated: boolean };

const NO_TEAM = "none";
/** Eine Zelle Gruppe × Zeile: Balken im Fenster und Zähler des geladenen Stands. */
type Cell = { bars: TimelineBar[]; loaded: number; loadedOverdue: number };

/** S7, 533: die Gruppen eines Tickets, Hauptgruppe zuerst — Team (privat) → Eigentümer-Team → alle Gruppen, in denen
 *  der Zuständige Mitglied ist (in der Folge `groupIds`) → „Ohne Team“. Nur sichtbare Gruppen zählen. */
export function groupsOf(card: TicketBoardCard, groupIds: readonly string[], membership: ReadonlyMap<string, readonly string[]>): string[] {
  const fixed = [card.team?.id, card.owner.kind === "TEAM" ? card.owner.team?.id : undefined]
    .find((id): id is string => id !== undefined && groupIds.includes(id));
  if (fixed !== undefined) return [fixed];
  const own = card.assignee === null ? [] : membership.get(card.assignee.id) ?? [];
  const groups = groupIds.filter((g) => own.includes(g));
  return groups.length > 0 ? groups : [NO_TEAM];
}

/** S7: die Hauptgruppe eines Tickets; „none“ = Ohne Team. */
export function groupOf(card: TicketBoardCard, groupIds: readonly string[], membership: ReadonlyMap<string, readonly string[]>): string {
  return groupsOf(card, groupIds, membership)[0]!;
}

/** Was `assignLanes` von einem Balken oder einer Marke liest und setzt. */
export type LaneItem = Pick<TimelineBar, "id" | "from" | "to" | "state" | "lane">;

const byRank = (a: LaneItem, b: LaneItem): number =>
  BAR_RANK[a.state] - BAR_RANK[b.state] || a.from.localeCompare(b.from) || a.id.localeCompare(b.id);

/** S8: Spuren gierig nach Rangfolge, jeder Balken in die erste Spur ohne Überlappung (ein gemeinsamer Tag genügt),
 *  höchstens `max`; setzt `bar.lane` (−1 = verborgen). `stretch` = die belegten Tage je Balken (Vorgabe `from`–`to`;
 *  die Zeitachse rechnet den Überzug bis heute mit). Gilt für Balken und Marken (D24) gleich. */
export function assignLanes<T extends LaneItem>(bars: T[], max = LANES_MAX,
    stretch: (b: T) => readonly [string, string] = (b) => [b.from, b.to]): { lanes: number; hidden: T[] } {
  const lanes: (readonly [string, string])[][] = [];
  const hidden: T[] = [];
  for (const bar of [...bars].sort(byRank)) {
    const [s, e] = stretch(bar);
    const free = lanes.findIndex((lane) => lane.every(([ls, le]) => e < ls || s > le));
    bar.lane = free >= 0 ? free : lanes.length < max ? lanes.push([]) - 1 : -1;
    if (bar.lane < 0) hidden.push(bar); else lanes[bar.lane]!.push([s, e]);
  }
  return { lanes: lanes.length, hidden };
}

const byName = (a: { name: string; id: string }, b: { name: string; id: string }): number =>
  a.name.localeCompare(b.name, "de") || a.id.localeCompare(b.id);

/** S5–S8, S13, S20 — Gruppen, Zeilen, Balken und Spuren für das Fenster. */
export function buildTimeline(e: TimelineInput): Timeline {
  const { heute, window: w } = e;
  const reach = (b: TimelineBar): string => (b.overdueFrom === null ? b.to : heute);     // Überzug bis heute
  const inWindow = (b: TimelineBar): boolean => b.from <= w.to && reach(b) >= w.from;    // Vorlauf zählt nicht
  const barOf = (card: TicketBoardCard, depth: number): TimelineBar => {
    const canExpand = card.subtaskTotal > 0 && Math.max(card.depth, depth) < DEPTH_MAX - 1;
    return { id: card.id, ...barEnds(card, heute), state: barState(card, heute), lane: -1, card, children: card.subtaskTotal,
             canExpand, expanded: canExpand && e.expanded.has(card.id), duplicateOf: null, isDuplicate: false,
             effort: effortOf(card) };
  };

  // S5: aktive Teams nach Name, dann stillgelegte; Mitgliedschaft zählt nur in aktiven Teams.
  const teams = [...e.rows.teams].sort((a, b) => Number(b.active) - Number(a.active) || byName(a, b));
  const groupIds = [...teams.map((t) => t.id), NO_TEAM];
  const membership = new Map<string, string[]>();
  const people = new Map<string, TimelinePerson>(e.rows.users.map((u) => [u.id, u]));
  for (const t of teams) for (const m of t.members) {
    people.set(m.id, m);
    if (t.active) membership.set(m.id, [...(membership.get(m.id) ?? []), t.id]);
  }
  const personOf = (card: TicketBoardCard): TimelinePerson | null => {
    const a = card.assignee;
    if (a === null) return null;
    if (!people.has(a.id)) people.set(a.id, { id: a.id, name: a.name, email: null });   // nur über die Karte bekannt (S2)
    return people.get(a.id)!;
  };

  // S20: die Balken im Fenster nach Rangfolge, höchstens BARS_MAX Tickets.
  const candidates = e.cards.map((c) => barOf(c, 0)).filter(inWindow).sort(byRank);
  const drawn = new Map(candidates.slice(0, BARS_MAX).map((b) => [b.id, b]));

  // S7: jede Karte in ihre Gruppen und dort in die Zeile ihres Zuständigen (null = Teamzeile). Die Zeile entsteht aus
  // jeder geladenen Karte, der Balken nur im Fenster.
  const cells = new Map(groupIds.map((g) => [g, new Map<string | null, Cell>()]));
  for (const card of e.cards) {
    const key = personOf(card)?.id ?? null;
    const bar = drawn.get(card.id);
    const late = barState(card, heute) === "OVERDUE" ? 1 : 0;
    const [main, ...more] = groupsOf(card, groupIds, membership) as [string, ...string[]];
    for (const g of [main, ...more]) {
      const row = cells.get(g)!;
      const cell = row.get(key) ?? { bars: [], loaded: 0, loadedOverdue: 0 };
      row.set(key, cell);
      cell.loaded += 1;
      cell.loadedOverdue += late;
      if (bar !== undefined) cell.bars.push(g === main ? bar : { ...bar, duplicateOf: main, isDuplicate: true });   // 533
    }
  }

  const makeRow = (id: string, kind: TimelineRowKind, label: string, person: TimelinePerson | null, depth: number,
                   { bars, loaded, loadedOverdue }: Cell, parentBarId: string | null): TimelineRow => {
    // Spur: Überzug bis heute, offenes Ende einen Rasterschritt (Tag) weiter — dort franst es aus (S9).
    const { lanes, hidden } = assignLanes(bars, LANES_MAX, (b) => [b.from, b.openEnd ? addDays(b.to, 1) : reach(b)]);
    return { id, kind, label, person, depth, bars: bars.filter((b) => b.lane >= 0).sort(byRank), lanes, hidden,
             overdue: bars.filter((b) => b.state === "OVERDUE").length, loaded, loadedOverdue, parentBarId,
             markers: [], hiddenMarkers: [], companyId: null };
  };
  // S13: Unterzeilen direkt hinter der Zeile des aufgeklappten Eltern-Balkens, in der Folge der Antwort (positionInParent).
  const expand = (bar: TimelineBar, parent: TimelineRow, out: TimelineRow[], path: ReadonlySet<string>): void => {
    if (!bar.expanded || path.has(bar.id)) return;
    for (const kid of (e.expanded.get(bar.id) ?? []).filter((k) => k.parentTicketId === bar.id)) {
      const kidBar = barOf(kid, parent.depth + 1);
      const row = makeRow(`${parent.id}:child:${kid.id}`, "CHILD", `${kid.displayNumber} ${kid.title}`, personOf(kid),
                          parent.depth + 1, { bars: inWindow(kidBar) ? [kidBar] : [], loaded: 1,
                            loadedOverdue: kidBar.state === "OVERDUE" ? 1 : 0 }, bar.id);
      out.push(row);
      expand(kidBar, row, out, new Set([...path, bar.id]));
    }
  };

  const teamFilter = e.filter?.team ?? "";
  const personFilter = (e.filter?.assignee ?? "") !== "";
  const showEmpty = !e.phone && !personFilter;                   // 536, S16: Zeilen ohne Ticket im geladenen Stand
  const groups: TimelineGroup[] = [];
  for (const team of [...teams, null]) {
    const id = team?.id ?? NO_TEAM;
    const row = cells.get(id)!;
    // S5: stillgelegte Teams und „Ohne Team“ nur mit Tickets im geladenen Stand (Ruling Aufgabe 6: kein leerer Kopf).
    if ((team?.active !== true && row.size === 0) || (teamFilter !== "" && id !== teamFilter)) continue;
    const gid = `team:${id}`;
    // S6: Teamzeile, ich, Mitglieder alphabetisch, zuletzt nur über Karten Bekannte.
    const members = team?.active ? team.members.map((m) => m.id) : [];
    const byPerson = (a: string, b: string) => byName(people.get(a)!, people.get(b)!);
    const others = [...row.keys()].filter((k): k is string => k !== null && !members.includes(k));
    const order = [null, ...[...members, ...others].filter((k) => k === e.meId),
                   ...members.filter((k) => k !== e.meId).sort(byPerson), ...others.filter((k) => k !== e.meId).sort(byPerson)];
    const rows: TimelineRow[] = [];
    for (const key of order) {
      const cell = row.get(key) ?? { bars: [], loaded: 0, loadedOverdue: 0 };
      // Teamzeile wie Personenzeile: entfällt nur ohne Ticket im geladenen Stand (Ruling Re-Review 3); leer „frei“ nie.
      if (cell.loaded === 0 && (key === null || !showEmpty)) continue;
      const person = key === null ? null : people.get(key)!;
      const r = makeRow(`row:${gid}:${key ?? "team"}`, key === null ? "TEAM" : "PERSON",
        person?.name ?? (team === null ? TIMELINE_TEXT.unassignedRow : TIMELINE_TEXT.teamRow), person, 0, cell, null);
      rows.push(r);
      // Nur sichtbare Eltern klappen auf: hinter „+n“ gäbe es keinen Aufklapper zum Zuklappen (Gesamt-Review K7); die
      // Klappstellung bleibt in `expanded`, die Unterzeilen kehren mit dem Balken zurück.
      for (const b of r.bars) expand(b, r, rows, new Set());
    }
    if (personFilter && rows.length === 0) continue;
    const own = rows.filter((r) => r.kind !== "CHILD");
    const stand = [...row.values()];
    groups.push({ id: gid, kind: "TEAM", source: null, team, label: team?.name ?? TIMELINE_TEXT.noTeam, rows,
      count: own.reduce((s, r) => s + r.bars.length + r.hidden.length, 0), overdue: own.reduce((s, r) => s + r.overdue, 0),
      loaded: stand.reduce((s, c) => s + c.loaded, 0), loadedOverdue: stand.reduce((s, c) => s + c.loadedOverdue, 0),
      collapsed: false });
  }

  // S20: ab der 9. Gruppe und sobald 300 Zeilen (Gruppenköpfe + offene Zeilen) stehen, starten weitere Gruppen
  // zugeklappt — die gemerkte Stellung geht vor (zu: `team:<id>`, ausdrücklich offen: `!team:<id>`).
  let used = 0, full = false;
  groups.forEach((g, i) => {
    full ||= i > 0 && used + 1 + g.rows.length > ROWS_MAX;
    g.collapsed = e.collapsed.has(g.id) || ((i >= GROUPS_OPEN_MAX || full) && !e.collapsed.has(`!${g.id}`));
    used += 1 + (g.collapsed ? 0 : g.rows.length);
  });
  const allRows = groups.flatMap((g) => g.rows);
  return { groups, rowCount: allRows.length,
           truncated: candidates.length > BARS_MAX || groups.length + allRows.length > ROWS_MAX,
           bars: new Set(allRows.flatMap((r) => [...r.bars, ...r.hidden]).map((b) => b.id)).size };
}

// ── Terminebene (Spec D24, D26, T19) ──────────────────────────────────────────────────────────────────────────

/** Ein Abruf `due-items` für das Fenster: höchstens die ersten 400 Tage (Review Focus 4 — Quartal plus Pinch oder
 *  Rahmen ergibt mehr; die Route antwortete sonst 422). `clipped` = gekürzt, die Ansicht sagt es. */
export function dueRequestWindow(w: TimelineWindow): { from: string; to: string; clipped: boolean } {
  const clipped = spanOf(w) > DUE_WINDOW_MAX_DAYS;
  return { from: w.from, to: clipped ? addDays(w.from, DUE_WINDOW_MAX_DAYS - 1) : w.to, clipped };
}

/** `companies` = Folge und Kürzel der Zeilen (wie die Unternehmen des Boards); `boardTicketIds` = Tickets im Board-Stand
 *  (D26: nur hinter einem dort gezeigten Fälligkeitsticket tritt ein Eintrag zurück, Review Aufgabe 2, H2). */
export type DueGroupsInput = { items: readonly DueItem[]; window: TimelineWindow; heute: string;
  collapsed: ReadonlySet<string>; boardTicketIds: ReadonlySet<string>;
  companies: readonly { companyId: string; shortCode: string }[] };

/** Die Marke eines Eintrags: am Tag, überfällig mit Überzug bis heute (wie der Balken, S9). */
function markerOf(item: DueItem, heute: string): TimelineMarker {
  const overdueFrom = item.state === "OVERDUE" && item.date < heute ? item.date : null;
  return { id: item.key, item, from: item.date, to: overdueFrom === null ? item.date : heute, state: item.state, lane: -1, overdueFrom };
}
const byDueRank = (a: TimelineMarker, b: TimelineMarker): number => compareDueItems(a.item, b.item);

/** D24: „Meilensteine“ (Kennung `milestones`, nur mit Meilensteinen im Fenster) und je Fremdquelle mit Marken im Fenster
 *  eine Gruppe `source:<SOURCE>` in Quellenfolge; Zeilen `row:<Gruppe>:<companyId>` je Unternehmen in der Folge
 *  `companies` (Unbekannte dahinter, Beschriftung aus dem Eintrag). Tickets sind Balken — ein `TICKET`-Eintrag und
 *  unbekannte Quellen (D13) fallen weg, ebenso Einträge, die hinter ihr Fälligkeitsticket zurücktreten (D26). Über
 *  `MARKERS_MAX` Marken die ersten nach Rang (D10), `truncated`; `markers` = gezeichnete Marken samt „+n“. Zugeklappt
 *  nach `collapsed` (gemerkt wie die Teamgruppen). */
export function buildDueGroups(e: DueGroupsInput): { milestones: TimelineGroup | null; sources: TimelineGroup[];
    markers: number; truncated: boolean } {
  const { window: w, heute } = e;
  const candidates = knownItems(e.items)
    .filter((i) => i.source !== "TICKET" && !recedesBehindTicket(i, e.boardTicketIds))
    .map((i) => markerOf(i, heute))
    .filter((m) => m.from <= w.to && m.to >= w.from)
    .sort(byDueRank);
  const drawn = candidates.slice(0, MARKERS_MAX);
  const known = new Map(e.companies.map((c, i) => [c.companyId, i]));
  const place = (id: string): number => known.get(id) ?? known.size;

  const groupOf = (source: DueSource): TimelineGroup | null => {
    const mine = drawn.filter((m) => m.item.source === source);
    if (mine.length === 0) return null;
    const milestones = source === "MILESTONE";
    const gid = milestones ? "milestones" : `source:${source}`;
    const companyIds = [...new Set(mine.map((m) => m.item.companyId))]
      .sort((a, b) => place(a) - place(b) || a.localeCompare(b));
    const rows = companyIds.map((companyId): TimelineRow => {
      const markers = mine.filter((m) => m.item.companyId === companyId);
      const { lanes, hidden } = assignLanes(markers, LANES_MAX, (m) => [m.from, m.to]);
      const late = markers.filter((m) => m.state === "OVERDUE").length;
      const label = e.companies.find((c) => c.companyId === companyId)?.shortCode ?? markers[0]!.item.companyShortCode;
      return { id: `row:${gid}:${companyId}`, kind: "DUE", label, person: null, depth: 0, bars: [], lanes, hidden: [],
               overdue: late, loaded: markers.length, loadedOverdue: late, parentBarId: null,
               markers: markers.filter((m) => m.lane >= 0), hiddenMarkers: hidden.sort(byDueRank), companyId };
    });
    const late = mine.filter((m) => m.state === "OVERDUE").length;
    return { id: gid, kind: milestones ? "MILESTONES" : "SOURCE", source, team: null,
             label: milestones ? TIMELINE_TEXT.milestonesGroup : TIMELINE_TEXT.sourceGroup[source], rows,
             count: mine.length, overdue: late, loaded: mine.length, loadedOverdue: late, collapsed: e.collapsed.has(gid) };
  };

  return { milestones: groupOf("MILESTONE"),
           sources: DUE_SOURCES.filter((s) => s !== "TICKET" && s !== "MILESTONE").flatMap((s) => groupOf(s) ?? []),
           markers: drawn.length, truncated: candidates.length > MARKERS_MAX };
}

// ── Layout (Review Focus 2) ────────────────────────────────────────────────────────────────────────────────────

/** Zeilenhöhen einer Gruppe (Spuren × 22 + 6, mindestens 28; `top` ab dem ersten Zeilenpixel, ohne Gruppenkopf) und
 *  ihre Teilung in Zeichenflächen von höchstens CANVAS_MAX_PX (`from`/`to` = Zeilenindex, `to` exklusiv). Unabhängig
 *  von `collapsed` — ob eine Gruppe gezeichnet wird, entscheidet die Ansicht. */
export function layout(group: TimelineGroup): { rows: { id: string; top: number; height: number }[]; totalPx: number;
    blocks: { from: number; to: number; top: number; height: number }[] } {
  let top = 0;
  const rows = group.rows.map((r) => {
    const height = Math.max(ROW_MIN_PX, r.lanes * LANE_PX + ROW_PAD_PX);
    top += height;
    return { id: r.id, top: top - height, height };
  });
  const blocks: { from: number; to: number; top: number; height: number }[] = [];
  rows.forEach((r, i) => {
    const last = blocks.at(-1);
    if (last !== undefined && last.height + r.height <= CANVAS_MAX_PX) { last.to = i + 1; last.height += r.height; }
    else blocks.push({ from: i, to: i + 1, top: r.top, height: r.height });
  });
  return { rows, totalPx: top, blocks };
}
